import { fs } from '@/theme/tokens';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import { useAuthStore } from '@/store/auth';
import { useEntityStore } from '@/store/entity';
import { useEntities } from '@/api/entities';
import { useMoneyFmt } from '@/lib/money';
import { useDashboardSummary } from '@/api/dashboard';
import { APP_BASE } from '@/lib/api-base';
import { HrmsSyncCard } from '@/components/hrms-sync/HrmsSyncCard';
import type { SvgIconComponent } from '@mui/icons-material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DescriptionIcon from '@mui/icons-material/Description';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaymentsIcon from '@mui/icons-material/Payments';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryIcon from '@mui/icons-material/History';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { SummaryStrip } from '@/components/TableKit';

const titleCase = (v: string) => v.charAt(0) + v.slice(1).toLowerCase();

/**
 * Admin dashboard — the landing page for staff logins. Driven by
 * GET /api/dashboard/summary (entity-scoped via the topbar selector).
 *
 * Surfaces the four things an operator checks first thing in the day:
 *   1. Headcount      — active vs total
 *   2. Latest run     — period, status, net payout
 *   3. Approvals      — what's waiting on a human (reimb / IT-decl / CTC / arrears)
 *   4. Compliance     — pending statutory challans
 * plus quick-links into the high-traffic modules.
 */

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  CALCULATED: 'info',
  APPROVED: 'success',
  LOCKED: 'success',
  PAID: 'success',
};

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const entityId = useEntityStore((s) => s.entityId);
  // Entity config-driven money formatter (respects rounding_precision /
  // number_format / currency_symbol from Payroll Config → Rounding).
  const inr = useMoneyFmt(entityId);
  const { data: entities } = useEntities();
  const activeEntity = entities?.find((e) => e.id === entityId);
  const { data, isLoading, error } = useDashboardSummary(entityId);
  const navigate = useNavigate();

  const approvalsHot = (data?.approvals.total ?? 0) > 0;
  // Starting a sync or confirming a mapping is for admins and HR; other
  // staff can still see the sync state and its history.
  const canSync = user?.role === 'ADMIN' || user?.role === 'HR_MANAGER';
  const challansHot = (data?.compliance.challans_pending ?? 0) > 0;
  const returnsLate = data?.compliance.returns_overdue ?? 0;

  const initials = (user?.full_name ?? 'Admin').trim().split(/\s+/).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '').join('');
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })
    .replace(/^(\w+) /, '$1, ');
  const pending = data?.compliance.challans_pending ?? 0;
  const overdue = data?.compliance.challans_overdue ?? 0;
  const approvals = data?.approvals.total ?? 0;
  const alert = !data ? null : challansHot ? {
    title: `${pending} statutory challan${pending === 1 ? '' : 's'} pending deposit`,
    sub: overdue ? `${overdue === 1 ? 'TDS deposit is' : `${overdue} TDS deposits are`} overdue.` : 'TDS deposit is due this month.',
    to: '/challan/entries',
  } : approvalsHot ? {
    title: `${approvals} item${approvals === 1 ? '' : 's'} awaiting action`,
    sub: 'Approvals are waiting on a reviewer.', to: '/approvals',
  } : returnsLate > 0 ? {
    title: `${returnsLate} TDS statement${returnsLate === 1 ? '' : 's'} past the filing due date`,
    sub: 'File the quarterly return.', to: '/statutory-returns',
  } : null;
  // A workspace created by self-service signup has no uKnowva to sync from.
  const showSync = user?.workspace_origin !== 'SIGNUP';

  return (
    <Box>
      {/* Greeting hero: initials, name, scope; date and FY on the right. */}
      <Box sx={{
        p: { xs: 2, sm: 2.5 }, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        border: '1px solid #d6e6fa', borderRadius: '12px',
        background: 'linear-gradient(135deg, #eaf3fe, #f6f9fe 60%)',
      }}>
        <Avatar sx={{
          width: 52, height: 52, bgcolor: 'primary.main', fontWeight: 700,
          boxShadow: '0 6px 16px rgba(26,115,232,.25)',
        }}>{initials || 'A'}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h1" sx={{ lineHeight: 1.2, mb: 0.25 }}>
            Welcome back{user?.full_name ? `, ${user.full_name}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeEntity
              ? <>Showing figures for <Box component="b" sx={{ color: 'text.primary', fontWeight: 600 }}>{activeEntity.name} ({activeEntity.short_code})</Box></>
              : 'Showing figures across all entities. Pick one from the top bar to scope.'}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: 'text.secondary' }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: fs.icon }} />
            <Typography variant="body2" color="text.secondary">{today}</Typography>
          </Stack>
          {data?.fiscal_year && (
            <Chip size="small" variant="outlined" label={`FY ${data.fiscal_year}`}
              sx={{ bgcolor: '#fff', borderColor: '#c9dcf6', color: 'primary.main', fontWeight: 700 }} />
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load the dashboard summary. {String((error as Error)?.message ?? error)}
        </Alert>
      )}

      {isLoading ? <Skeleton variant="rounded" width={560} height={44} sx={{ mb: 2 }} /> : (
        <SummaryStrip items={[
          { label: 'Headcount', value: data?.headcount.active ?? 0, hint: `${data?.headcount.total ?? 0} total`,
            onClick: () => navigate(`${APP_BASE}/employees`) },
          { label: 'Latest run', onClick: () => navigate(`${APP_BASE}/payroll-runs`),
            value: data?.latest_run
              ? <>{data.latest_run.period_label} <Chip size="small" color={STATUS_COLOR[data.latest_run.status] ?? 'default'}
                  label={titleCase(data.latest_run.status)} sx={{ ml: 0.5, verticalAlign: 1 }} /></>
              : '—',
            hint: data?.latest_run ? `Net ${inr(data.latest_run.total_net)} · ${data.latest_run.employee_count} employees` : 'No runs yet' },
          { label: 'Awaiting action', value: approvals, onClick: () => navigate(`${APP_BASE}/approvals`) },
          { label: 'Challans pending', onClick: () => navigate(`${APP_BASE}/challan/entries`),
            value: <>{pending}{overdue > 0 && (
              <Box component="span" sx={{ ml: 0.75, fontSize: fs.small, fontWeight: 600, color: '#b42318' }}>{overdue} overdue</Box>
            )}</> },
        ]} />
      )}

      {/* Alert + uKnowva sync, side by side. */}
      {(alert || showSync) && (
        <Box sx={{
          display: 'grid', gap: 1.5, mb: 1.5,
          gridTemplateColumns: { xs: '1fr', md: alert && showSync ? 'repeat(2, minmax(0, 1fr))' : '1fr' },
        }}>
          {alert && (
            <Box component={RouterLink} to={`${APP_BASE}${alert.to}`} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.5, borderRadius: '12px',
              textDecoration: 'none', color: 'inherit', bgcolor: '#fff8ec', border: '1px solid #f3d9ae',
              transition: 'box-shadow 140ms', '&:hover': { boxShadow: '0 4px 14px rgba(200,137,26,0.16)' },
            }}>
              <Box sx={{
                flexShrink: 0, width: 36, height: 36, borderRadius: '10px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', bgcolor: '#fbe7c6', color: '#b87b0a',
              }}>
                <WarningAmberIcon sx={{ fontSize: fs.title }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>{alert.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{alert.sub}</Typography>
              </Box>
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, fontWeight: 700, color: '#a86a06' }}>
                Review <ChevronRightIcon sx={{ fontSize: fs.xl }} />
              </Stack>
            </Box>
          )}
          {showSync && <HrmsSyncCard canEdit={canSync} compact />}
        </Box>
      )}

      {/* Approvals breakdown + quick links */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <DashCard title="What needs attention" aside={!isLoading && approvals === 0 && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#178a55' }}>
            <CheckCircleIcon sx={{ fontSize: fs.icon }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>All clear</Typography>
          </Stack>
        )}>
          <AttnRow label="Reimbursements to approve or reject" to="/reimbursements" icon={ReceiptLongIcon} tone="o"
            n={data?.approvals.reimbursements_pending} loading={isLoading} />
          <AttnRow label="Reimbursements awaiting payout" to="/reimbursements" icon={PaymentsIcon} tone="g"
            n={data?.approvals.reimbursements_awaiting_payout} loading={isLoading} />
          <AttnRow label="IT declarations to review" to="/it-declarations" icon={DescriptionIcon} tone="b"
            n={data?.approvals.it_declarations_pending} loading={isLoading} />
          <AttnRow label="CTC revisions to approve" to="/approvals" icon={TrendingUpIcon} tone="v"
            n={data?.approvals.ctc_revisions_pending} loading={isLoading} />
          <AttnRow label="Arrears to apply to a run" to="/arrears" icon={HistoryIcon} tone="t"
            n={data?.approvals.arrears_computed} loading={isLoading} />
        </DashCard>

        <DashCard title="Quick links">
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.25, mt: 1 }}>
            {QUICK_LINKS.map((q) => <QuickTile key={q.to} {...q} />)}
          </Box>
        </DashCard>
      </Box>
    </Box>
  );
}

// ── Quick-link config ─────────────────────────────────────────────────
const QUICK_LINKS: { to: string; label: string; ic: SvgIconComponent; tone: Tone }[] = [
  { to: '/payroll-runs', label: 'Payroll Runs', ic: PlayCircleIcon, tone: 'b' },
  { to: '/employees', label: 'Employees', ic: GroupsIcon, tone: 'v' },
  { to: '/attendance', label: 'Attendance', ic: EventAvailableIcon, tone: 'g' },
  { to: '/reimbursements', label: 'Reimbursements', ic: ReceiptLongIcon, tone: 'o' },
  { to: '/it-declarations', label: 'IT Declarations', ic: HistoryEduIcon, tone: 't' },
  { to: '/reports', label: 'Reports', ic: BarChartIcon, tone: 'p' },
];

// ── Presentational helpers ──────────────────────────────────────────────
/** Soft icon tiles from the wireframe: tint ground, solid glyph. */
type Tone = 'b' | 'v' | 'g' | 'o' | 't' | 'p';
const TONE: Record<Tone, { bg: string; fg: string }> = {
  b: { bg: '#e5effd', fg: '#2a64c8' },
  v: { bg: '#ece7fb', fg: '#6a4fc9' },
  g: { bg: '#dcf4e8', fg: '#178a55' },
  o: { bg: '#fdeedd', fg: '#c06a0e' },
  t: { bg: '#dcf4f2', fg: '#0e8a80' },
  p: { bg: '#fde8f1', fg: '#b8407a' },
};

function DashCard({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{
      border: 1, borderColor: 'divider', borderRadius: '12px', px: 2.25, py: 1.75, bgcolor: '#fff',
      boxShadow: '0 1px 2px rgba(16,24,40,.04)',
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography variant="h3">{title}</Typography>
        {aside}
      </Stack>
      {children}
    </Box>
  );
}

function AttnRow({
  label, to, n, loading, icon: Ico, tone,
}: { label: string; to: string; n?: number; loading: boolean; icon: SvgIconComponent; tone: Tone }) {
  const count = n ?? 0;
  const hot = count > 0;
  return (
    <Box component={RouterLink} to={`${APP_BASE}${to}`} sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, textDecoration: 'none', color: 'inherit',
      borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 },
      '&:hover .attn-label': { color: 'primary.main' },
    }}>
      <Box sx={{
        flexShrink: 0, width: 30, height: 30, borderRadius: '8px', display: 'flex',
        alignItems: 'center', justifyContent: 'center', bgcolor: TONE[tone].bg,
      }}>
        <Ico sx={{ fontSize: fs.icon, color: TONE[tone].fg }} />
      </Box>
      <Typography className="attn-label" variant="body2" sx={{ flex: 1, minWidth: 0 }}>{label}</Typography>
      {loading ? <Skeleton width={28} height={22} /> : (
        <Box sx={{
          minWidth: 26, height: 22, px: 1, borderRadius: '11px', display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', fontSize: fs.small, fontWeight: 700,
          bgcolor: hot ? '#fde5e3' : '#f2f4f7', color: hot ? '#b42318' : 'text.disabled',
        }}>{count}</Box>
      )}
    </Box>
  );
}

function QuickTile({ to, label, ic: Ico, tone }: { to: string; label: string; ic: SvgIconComponent; tone: Tone }) {
  return (
    <Box component={RouterLink} to={`${APP_BASE}${to}`} sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1.75, px: 1,
      borderRadius: '10px', border: 1, borderColor: 'divider', textDecoration: 'none', color: 'inherit',
      textAlign: 'center', background: 'linear-gradient(180deg, #fff, #fafbfd)',
      transition: 'border-color 130ms, box-shadow 130ms',
      '&:hover': { borderColor: TONE[tone].fg, boxShadow: '0 4px 12px rgba(16,24,40,.06)' },
    }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: '12px', display: 'flex',
        alignItems: 'center', justifyContent: 'center', bgcolor: TONE[tone].bg,
      }}>
        <Ico sx={{ fontSize: fs.title, color: TONE[tone].fg }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}
